import { For, Show, createMemo, createSignal } from "solid-js";

import { useNotificationPreferences, useUpdateNotificationPreferences } from "~/lib/hooks";
import type { NotificationKindPref, NotificationPreferenceUpdate } from "~/lib/types";

type Props = {
  wsKey: () => string;
};

// Friendly labels for each kind. Server is the source of truth for the
// set; this map just adds human-readable copy. Unknown kinds (added
// server-side ahead of client deploys) render with the raw key.
const KIND_LABELS: Record<string, { title: string; description: string }> = {
  agent_batch_completed: {
    title: "Agent batch completion",
    description:
      "When you trigger a workspace-wide scan (dedup, lead scoring), notify you on completion with a summary.",
  },
  review_queue_pending: {
    title: "Review queue pending",
    description: "Daily digest of items waiting for your approval in the Review Queue.",
  },
  deal_stalled: {
    title: "Deal stalled",
    description: "When a deal you own hasn't had activity for the workspace's stalled threshold.",
  },
  task_due_soon: {
    title: "Task due soon",
    description: "Tasks assigned to you that are due in the next 24 hours.",
  },
  mention: {
    title: "Mention",
    description: "When another user @mentions you in a note or activity (when mentions ship).",
  },
};

const CHANNELS: { value: string; label: string; available: boolean; note?: string }[] = [
  { value: "in_app", label: "In-app", available: true },
  {
    value: "email",
    label: "Email",
    available: false,
    note: "Pending ScaiSend integration",
  },
];

export function NotificationPreferencesPanel(props: Props) {
  const prefs = useNotificationPreferences(props.wsKey);
  const update = useUpdateNotificationPreferences(props.wsKey);
  const [error, setError] = createSignal<string | null>(null);

  // Local working copy. Hydrates from server on first paint; mutations
  // optimistically update so the toggles feel responsive.
  const [draft, setDraft] = createSignal<Record<string, NotificationKindPref> | null>(null);
  const effective = createMemo(() => draft() ?? prefs.data?.prefs ?? {});

  const togglePref = async (kind: string, patch: Partial<NotificationKindPref>) => {
    const current = effective()[kind] ?? { enabled: true, channels: ["in_app"] };
    const next: NotificationKindPref = {
      enabled: patch.enabled ?? current.enabled,
      channels: patch.channels ?? current.channels,
    };
    const merged = { ...effective(), [kind]: next };
    setDraft(merged);
    setError(null);
    const body: NotificationPreferenceUpdate = { prefs: merged };
    try {
      await update.mutateAsync(body);
    } catch (err) {
      setError((err as Error).message);
      setDraft(null); // revert by clearing draft so server view shows again
    }
  };

  const toggleChannel = (kind: string, channel: string, on: boolean) => {
    const current = effective()[kind] ?? { enabled: true, channels: ["in_app"] };
    const channels = on
      ? Array.from(new Set([...current.channels, channel]))
      : current.channels.filter((c) => c !== channel);
    return togglePref(kind, { channels });
  };

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <div>
          <h3 class="card-title text-base">🔔 Notifications</h3>
          <p class="text-xs opacity-60 mt-0.5">
            Pick which notifications you receive and where. Email delivery arrives when ScaiSend
            integration lands.
          </p>
        </div>

        <Show when={prefs.isLoading}>
          <p class="text-sm opacity-60 mt-2">Loading…</p>
        </Show>

        <Show when={error()}>
          <div role="alert" class="alert alert-error text-sm mt-2">
            {error()}
          </div>
        </Show>

        <div class="overflow-x-auto mt-2">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Kind</th>
                <th class="w-24">Enabled</th>
                <For each={CHANNELS}>
                  {(c) => (
                    <th class="w-24" title={c.note}>
                      {c.label}
                      <Show when={!c.available}>
                        <span class="badge badge-ghost badge-xs ml-1">soon</span>
                      </Show>
                    </th>
                  )}
                </For>
              </tr>
            </thead>
            <tbody>
              <For each={Object.entries(effective())}>
                {([kind, pref]) => {
                  const label = KIND_LABELS[kind] ?? {
                    title: kind,
                    description: "",
                  };
                  return (
                    <tr>
                      <td>
                        <div class="font-medium">{label.title}</div>
                        <Show when={label.description}>
                          <div class="text-xs opacity-60 max-w-md">{label.description}</div>
                        </Show>
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          class="toggle toggle-sm toggle-primary"
                          checked={pref.enabled}
                          aria-label={`Enable ${label.title}`}
                          onChange={(e) =>
                            void togglePref(kind, { enabled: e.currentTarget.checked })
                          }
                        />
                      </td>
                      <For each={CHANNELS}>
                        {(c) => (
                          <td>
                            <input
                              type="checkbox"
                              class="checkbox checkbox-sm"
                              checked={pref.channels.includes(c.value)}
                              disabled={!c.available || !pref.enabled}
                              aria-label={`${c.label} channel for ${label.title}`}
                              onChange={(e) =>
                                void toggleChannel(kind, c.value, e.currentTarget.checked)
                              }
                            />
                          </td>
                        )}
                      </For>
                    </tr>
                  );
                }}
              </For>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
